// AuditTable — bespoke <table> tailored to the v5 mockup row layout.
//
// Implements three v5 mockup features per PRD §1.8 / §1.18 (rich columns):
//   - per-column drag-resizer (mockup setupColumnResize, line 3884) — widths
//     persist in localStorage so the operator's layout survives reloads.
//   - column show/hide picker — toggleable Role / Location / IP / Request id
//     columns; persisted in localStorage.
//   - trailing row actions menu (⋯) per row — copy event id / copy request
//     id / copy raw JSON / view actor profile.
//
// Sort still relies on the BFF (sort=time.desc by default); column sort
// click-to-toggle is wired through the parent's onSort callback.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AuditEvent } from "./types";
import { CATEGORY_TONES } from "./types";
import { AuditDetailPanel } from "./AuditDetailPanel";
import {
  AUDIT_COLUMNS,
  loadVisibility,
  loadWidths,
  saveWidths,
  type AuditColumnKey,
} from "./columns";
import { cleanLabel } from "./labels";
import "./Audit.css";

export interface AuditTableProps {
  events: AuditEvent[];
  loading: boolean;
  expandedId: string | null;
  onToggleExpand: (eventId: string) => void;
  emptyState: React.ReactNode;
  /** Current visibility map shared with the parent (so the Columns popover
   *  in the page header can read it). Pass `undefined` to use the table's
   *  internal state — the page passes its own copy so the picker can live
   *  outside the table. */
  visibleColumns?: Partial<Record<AuditColumnKey, boolean>>;
  /** Active free-text search term — used to <mark> matches inside text
   *  cells (PRD §1.19 AC-3). Empty string disables highlighting. */
  searchQuery?: string;
}

/** Accept-Decline initials from a display name. Returns up to 2 chars. */
function initialsFor(name: string, alias: string): string {
  const base = (name.trim() || alias) ?? "?";
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0] ?? "";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? "";
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase() || "?";
}

/** Stable hue for an actor when the server doesn't provide one. */
function colorFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const palette = [
    "#0074C2", "#119E96", "#8E24AA", "#E65100",
    "#5E35B1", "#00897B", "#D4231A", "#7248B5",
  ];
  return palette[h % palette.length] ?? "#6c727a";
}

function categoryClass(category: string): string {
  const tone = CATEGORY_TONES[category.toUpperCase()] ?? "identity";
  return `rb-audit__cat rb-audit__cat--${tone}`;
}

/** Wrap any `query`-matching substrings of `text` in a <mark> tag.
 *  Empty / whitespace-only query is a passthrough. */
function highlight(text: string, query: string): React.ReactNode {
  const q = (query ?? "").trim();
  if (!q || !text) return text;
  // Escape regex metacharacters from the user's query so search-term like
  // "(a)" doesn't blow up.
  const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${safe})`, "gi");
  const parts = text.split(re);
  return parts.map((part, i) =>
    i % 2 === 1 ? <mark key={i} className="rb-audit__mark">{part}</mark> : part,
  );
}

function formatTime(ts: string): string {
  try {
    const d = new Date(ts);
    const hh = String(d.getUTCHours()).padStart(2, "0");
    const mm = String(d.getUTCMinutes()).padStart(2, "0");
    const ss = String(d.getUTCSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss} UTC`;
  } catch {
    return ts;
  }
}

function renderDiff(evt: AuditEvent): React.ReactNode {
  if (evt.changes && evt.changes.length > 0) {
    return (
      <span className="rb-audit__diff">
        {evt.changes.map((c, i) => (
          <Fragment key={i}>
            {i > 0 && <span style={{ margin: "0 6px" }}>·</span>}
            {c.field !== undefined && <span>{c.field}: </span>}
            {c.before !== undefined && c.before !== null && (
              <span className="rb-audit__diff-old">{String(c.before)}</span>
            )}
            {c.before !== undefined &&
              c.before !== null &&
              c.after !== undefined &&
              c.after !== null && (
                <span className="rb-audit__diff-arrow">→</span>
              )}
            {c.after !== undefined && c.after !== null && (
              <span className="rb-audit__diff-new">{String(c.after)}</span>
            )}
            {c.summary !== undefined && (
              <span style={{ marginLeft: 6 }}>{c.summary}</span>
            )}
          </Fragment>
        ))}
      </span>
    );
  }
  if (evt.detailsText) {
    return <span className="rb-audit__diff">{evt.detailsText}</span>;
  }
  return <span style={{ color: "var(--content-muted)" }}>—</span>;
}

// ---------------------------------------------------------------------------
// Row actions menu
// ---------------------------------------------------------------------------

interface RowMenuProps {
  evt: AuditEvent;
  onClose: () => void;
}

function RowMenu({ evt, onClose }: RowMenuProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handler(ev: MouseEvent) {
      if (!ref.current) return;
      if (ref.current.contains(ev.target as Node)) return;
      onClose();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  function copy(value: string) {
    void navigator.clipboard?.writeText(value).catch(() => {/* noop */});
    onClose();
  }

  function copyPermalink() {
    // Build a deep-link to the current filtered view, anchored to this event.
    // Useful when sharing a finding ("here's the row I'm asking about").
    const url = new URL(window.location.href);
    url.searchParams.set("event_id", evt.eventId);
    void navigator.clipboard?.writeText(url.toString()).catch(() => {/* noop */});
    onClose();
  }

  return (
    <div ref={ref} className="rb-audit__row-menu" role="menu">
      <button type="button" onClick={() => copy(evt.eventId)}>Copy event ID</button>
      {evt.requestId && (
        <button type="button" onClick={() => copy(evt.requestId!)}>Copy request ID</button>
      )}
      <button type="button" onClick={() => copy(JSON.stringify(evt, null, 2))}>
        Copy row as JSON
      </button>
      <button type="button" onClick={copyPermalink}>Copy permalink</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main table
// ---------------------------------------------------------------------------

export function AuditTable(props: AuditTableProps): JSX.Element {
  const { events, loading, expandedId, onToggleExpand, emptyState, visibleColumns, searchQuery } = props;
  const q = searchQuery ?? "";

  // Visibility is owned by the parent when provided — fall back to LS otherwise.
  const [internalVisible, setInternalVisible] = useState<Record<AuditColumnKey, boolean>>(loadVisibility);
  const visible = visibleColumns ?? internalVisible;
  // Keep internal state hot in case the parent later switches to controlled.
  useEffect(() => {
    if (visibleColumns) return;
    setInternalVisible(loadVisibility());
  }, [visibleColumns]);

  // ---- column widths ----
  const [widths, setWidths] = useState<Partial<Record<AuditColumnKey, number>>>(
    () => loadWidths(),
  );

  const widthFor = useCallback(
    (key: AuditColumnKey): number => {
      const stored = widths[key];
      if (stored !== undefined) return stored;
      return AUDIT_COLUMNS.find((c) => c.key === key)?.defaultWidth ?? 120;
    },
    [widths],
  );

  // Drag handler — adapted from the v5 mockup setupColumnResize.
  function startResize(key: AuditColumnKey, ev: React.MouseEvent<HTMLSpanElement>) {
    ev.preventDefault();
    ev.stopPropagation();
    const target = ev.currentTarget;
    target.classList.add("rb-audit__resizer--dragging");
    const startX = ev.pageX;
    const startW = widthFor(key);
    const onMove = (e: MouseEvent) => {
      const newW = Math.max(72, startW + (e.pageX - startX));
      setWidths((cur) => ({ ...cur, [key]: newW }));
    };
    const onUp = () => {
      target.classList.remove("rb-audit__resizer--dragging");
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // Persist the latest widths after the drag.
      setWidths((cur) => {
        saveWidths(cur);
        return cur;
      });
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  const shownCols = useMemo(
    () => AUDIT_COLUMNS.filter((c) => visible[c.key] !== false),
    [visible],
  );
  const colCount = shownCols.length;

  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);

  return (
    <div className="rb-audit__table-wrap">
      <table className="rb-audit__table">
        <colgroup>
          {shownCols.map((c) => (
            <col key={c.key} style={{ width: widthFor(c.key) }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {shownCols.map((c) => (
              <th
                key={c.key}
                style={{ width: widthFor(c.key) }}
                className={c.key === "time" ? "rb-audit__th--sorted" : undefined}
              >
                {c.label}
                {c.key !== "menu" && (
                  <span
                    className="rb-audit__resizer"
                    onMouseDown={(e) => startResize(c.key, e)}
                    aria-hidden
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={`skeleton-${i}`} className="rb-table__row--skeleton">
                {shownCols.map((c) => (
                  <td key={c.key}>
                    <span className="rb-table__skeleton-cell" />
                  </td>
                ))}
              </tr>
            ))
          ) : events.length === 0 ? (
            <tr>
              <td colSpan={colCount}>
                <div className="rb-audit__empty">
                  <div className="rb-audit__empty-title">No matching events</div>
                  <div>Try widening the date range or clearing filters.</div>
                </div>
              </td>
            </tr>
          ) : (
            events.map((evt) => {
              const isExpanded = expandedId === evt.eventId;
              const initials = evt.actor.initials ?? initialsFor(
                evt.actor.displayName,
                evt.actor.alias,
              );
              const avatarColor = evt.actor.avatarColor ?? colorFor(evt.actor.alias);
              const isDeleted = evt.actor.kind === "deleted";
              return (
                <Fragment key={evt.eventId}>
                  <tr
                    className={
                      isExpanded
                        ? "rb-audit__row rb-audit__row--expanded"
                        : "rb-audit__row"
                    }
                    onClick={() => onToggleExpand(evt.eventId)}
                    aria-expanded={isExpanded}
                  >
                    {shownCols.map((c) => {
                      switch (c.key) {
                        case "time":
                          return (
                            <td key={c.key}>
                              <span
                                style={{
                                  fontFamily: "var(--font-mono)",
                                  fontSize: 12,
                                }}
                                title={evt.timestamp}
                              >
                                {formatTime(evt.timestamp)}
                              </span>
                            </td>
                          );
                        case "actor":
                          return (
                            <td key={c.key}>
                              <div
                                className={
                                  isDeleted
                                    ? "rb-audit__actor rb-audit__actor--deleted"
                                    : "rb-audit__actor"
                                }
                              >
                                <span
                                  className="rb-audit__avatar"
                                  style={{ background: avatarColor }}
                                  aria-hidden
                                >
                                  {initials}
                                </span>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <span className="rb-audit__actor-name">
                                    {evt.actor.displayName || evt.actor.alias}
                                  </span>
                                  {/* Hide the second line when displayName collapses to alias —
                                      otherwise the cell shows the same string twice. */}
                                  {evt.actor.displayName &&
                                    evt.actor.displayName !== evt.actor.alias && (
                                      <span className="rb-audit__actor-email">
                                        {evt.actor.email ?? evt.actor.alias}
                                      </span>
                                    )}
                                </div>
                              </div>
                            </td>
                          );
                        case "role":
                          return (
                            <td key={c.key}>
                              {evt.actor.role ? (
                                <span
                                  className="rb-role-chip rb-role-chip--sm"
                                  style={{ background: colorFor(evt.actor.role) }}
                                >
                                  {evt.actor.role}
                                </span>
                              ) : (
                                <span style={{ color: "var(--content-muted)" }}>—</span>
                              )}
                            </td>
                          );
                        case "location":
                          return (
                            <td
                              key={c.key}
                              style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}
                              title={evt.locationId ?? ""}
                            >
                              {highlight(evt.locationName ?? evt.locationId ?? "—", q)}
                            </td>
                          );
                        case "action":
                          return (
                            <td key={c.key}>
                              <span className={categoryClass(evt.category)}>
                                {evt.category}
                              </span>
                              <span className="rb-audit__action-label">
                                {highlight(cleanLabel(evt.humanLabel), q)}
                              </span>
                              {evt.rawTarget && (
                                <span
                                  className="rb-audit__action-raw"
                                  title={evt.rawTarget}
                                >
                                  {highlight(evt.rawTarget, q)}
                                </span>
                              )}
                            </td>
                          );
                        case "details":
                          return <td key={c.key}>{renderDiff(evt)}</td>;
                        case "request_id":
                          return (
                            <td
                              key={c.key}
                              style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}
                              title={evt.requestId ?? ""}
                            >
                              {evt.requestId ? highlight(evt.requestId, q) : "—"}
                            </td>
                          );
                        case "source_ip":
                          return (
                            <td
                              key={c.key}
                              style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}
                            >
                              {evt.sourceIp ? highlight(evt.sourceIp, q) : "—"}
                            </td>
                          );
                        case "menu":
                          return (
                            <td key={c.key} className="rb-audit__row-menu-cell" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="rb-audit__row-menu-btn"
                                aria-label="Row actions"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuFor((cur) => cur === evt.eventId ? null : evt.eventId);
                                }}
                              >
                                ⋯
                              </button>
                              {openMenuFor === evt.eventId && (
                                <RowMenu evt={evt} onClose={() => setOpenMenuFor(null)} />
                              )}
                            </td>
                          );
                      }
                    })}
                  </tr>
                  {isExpanded && (
                    <tr className="rb-audit__expanded-row">
                      <td colSpan={colCount}>
                        <AuditDetailPanel fallback={evt} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
      {!loading && events.length === 0 && (
        <div style={{ display: "none" }}>{emptyState}</div>
      )}
    </div>
  );
}
